/**
 * PR-CORE-05 / BLOCKER C — Hindi-first first-run onboarding for Web.
 *
 * Shown only while the canonical membership flag `onboarding_done` is false
 * (POST /api/auth/onboarding-complete persists it — no second user-profile
 * state). Every claim here is derived from real data: account status,
 * sanitized ProductRuntimeTruth (GET /api/settings), and the public desktop
 * release metadata. Nothing claims availability that truth does not back.
 * Hindi is the primary copy; English parity sits under each line.
 */
import { useEffect, useMemo, useState } from "react";
import { sanitizePublicProductTruth } from "@smart-shikka/core";
import { CapabilityStateBadge } from "@smart-shikka/react";
import { api, COOKIE_SESSION_TOKEN, type PublicAccount } from "../api.js";
import { useAuth } from "../auth.js";
import { useLocale } from "../ui.js";

interface OnboardingProps {
  account: PublicAccount;
  onDone: () => void;
}

interface DesktopRelease {
  version: string;
  installerUrl: string;
  installerSha256?: string | null;
  releaseNotesUrl?: string | null;
  signatureVerified?: boolean;
  signer?: string;
}

function t(locale: string, hi: string, en: string): string {
  return locale === "hi" ? hi : en;
}

export function FirstRunOnboarding({ account, onDone }: OnboardingProps): React.JSX.Element {
  const { locale } = useLocale();
  const { refresh } = useAuth();
  const [entries, setEntries] = useState<readonly { capabilityId: string; state: string }[]>([]);
  const [truthReady, setTruthReady] = useState(false);
  const [release, setRelease] = useState<DesktopRelease | "unavailable" | "loading">("loading");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch("/api/settings", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : undefined))
      .then((payload: { productTruth?: unknown } | undefined) => {
        if (cancelled) return;
        const truth = sanitizePublicProductTruth(payload?.productTruth);
        if (truth) setEntries(truth.entries);
        setTruthReady(true);
      })
      .catch(() => {
        if (!cancelled) setTruthReady(true);
      });
    api
      .publicDesktopRelease()
      .then((res) => {
        // Public policy: only a signature-VERIFIED release may be advertised.
        const ok = res.ok && res.data?.installerUrl && res.data.signatureVerified === true;
        if (!cancelled) setRelease(ok ? res.data! : "unavailable");
      })
      .catch(() => {
        if (!cancelled) setRelease("unavailable");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const stateOf = useMemo(() => {
    const map = new Map(entries.map((e) => [e.capabilityId, e.state]));
    return (id: string) => map.get(id);
  }, [entries]);

  const legend: readonly string[] = [
    "LIVE",
    "APPROVAL_REQUIRED",
    "PREVIEW",
    "NOT_CONFIGURED",
    "OWNER_ACTION_REQUIRED",
    "BLOCKED",
  ];

  const complete = async () => {
    setSaving(true);
    const res = await api.completeOnboarding(COOKIE_SESSION_TOKEN);
    if (res.ok) {
      await refresh();
      onDone();
    } else {
      setSaveError(true);
      setSaving(false);
    }
  };

  const approved = account.status === "active";
  const row: React.CSSProperties = {
    display: "flex",
    gap: 10,
    alignItems: "baseline",
    justifyContent: "space-between",
    padding: "8px 0",
    borderBottom: "1px solid var(--ss-border, #e2e8f0)",
    flexWrap: "wrap",
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t(locale, "पहली बार स्वागत", "First-run welcome")}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(2, 6, 23, 0.72)",
        display: "grid",
        placeItems: "center",
        zIndex: 1000,
        padding: 16,
        overflow: "auto",
      }}
    >
      <section
        style={{
          background: "var(--ss-card, #fff)",
          color: "var(--ss-fg, #0f172a)",
          borderRadius: 14,
          maxWidth: 620,
          width: "100%",
          padding: "clamp(18px, 4vw, 32px)",
          maxHeight: "calc(100vh - 32px)",
          overflow: "auto",
        }}
      >
        <h2 style={{ margin: "0 0 4px", fontSize: 22 }}>
          {t(locale, "Smart Shikka में आपका स्वागत है", "Welcome to Smart Shikka")}
        </h2>
        <p style={{ margin: "0 0 16px", color: "var(--ss-muted, #64748b)", fontSize: 13 }}>
          {t(
            locale,
            "पहली बार की जानकारी — सब कुछ असल स्थिति पर आधारित है, कोई अनुमान नहीं।",
            "First-run facts — everything below reflects real status, nothing is assumed.",
          )}
        </p>

        <div>
          <div style={row}>
            <strong>{t(locale, "आपका खाता", "Your account")}</strong>
            <span>
              {account.email} · {account.role} ·{" "}
              {approved
                ? t(locale, "सक्रिय — owner approval मिल गया है", "active — owner approval granted")
                : t(locale, "अनुमति लंबित", "approval pending")}
            </span>
          </div>
          <div style={row}>
            <strong>{t(locale, "Web अभी क्या कर सकता है", "What Web can do now")}</strong>
            <span style={{ textAlign: "right", fontSize: 13 }}>
              {truthReady
                ? t(
                    locale,
                    "नीचे क्षमता-स्थिति देखें — चैट और सेटिंग्स सीधे उपलब्ध हैं; बाकी स्थिति बैज पर लिखी है।",
                    "See capability states below — chat and settings are directly reachable; the rest is printed on each badge.",
                  )
                : t(locale, "स्थिति लोड हो रही है…", "Loading status…")}
            </span>
          </div>
          {["streaming-conversation", "agent-skills", "external-app-adapters", "browser-control"].map(
            (id) => (
              <div key={id} style={row}>
                <span style={{ fontSize: 13 }}>{id}</span>
                <CapabilityStateBadge
                  status={stateOf(id) ?? "NOT_CONFIGURED"}
                  locale={locale === "hi" ? "hi" : "en"}
                />
              </div>
            ),
          )}
          <div style={row}>
            <strong>{t(locale, "Desktop कब चाहिए", "When Desktop is required")}</strong>
            <span style={{ textAlign: "right", fontSize: 13 }}>
              {t(
                locale,
                "स्थानीय फ़ाइल/browser नियंत्रण जैसे कार्य केवल paired Desktop से चलते हैं — Web में वे सतहें निष्क्रिय दिखती हैं।",
                "Local file/browser control runs only through a paired Desktop — those surfaces stay inactive on Web.",
              )}
            </span>
          </div>
          <div style={row}>
            <strong>{t(locale, "Windows installer", "Windows installer")}</strong>
            <span style={{ textAlign: "right", fontSize: 13 }}>
              {release === "loading"
                ? t(locale, "जाँच जारी…", "Checking…")
                : release === "unavailable"
                  ? t(
                      locale,
                      "अभी release metadata उपलब्ध नहीं — installer उपलब्ध मानकर न चलें।",
                      "Release metadata unavailable right now — do not assume the installer is published.",
                    )
                  : t(
                      locale,
                      `v${release.version} उपलब्ध — signature verified${release.signer ? ` (${release.signer})` : ""}${release.installerSha256 ? " · SHA256 उपलब्ध" : ""}`,
                      `v${release.version} available — signature verified${release.signer ? ` (${release.signer})` : ""}${release.installerSha256 ? " · SHA256 provided" : ""}`,
                    )}
            </span>
          </div>
          <div style={row}>
            <strong>{t(locale, "Provider / model", "Provider / model")}</strong>
            <span style={{ textAlign: "right", fontSize: 13 }}>
              {t(
                locale,
                "Settings → Providers से owner गोपनीय key configure करता है; कुंजियाँ कभी UI में नहीं दिखतीं।",
                "The owner configures provider keys under Settings → Providers; keys never render in the UI.",
              )}
            </span>
          </div>
          <div style={row}>
            <strong>{t(locale, "Approvals + Emergency Stop", "Approvals + Emergency Stop")}</strong>
            <span style={{ textAlign: "right", fontSize: 13 }}>
              {t(
                locale,
                "संवेदनशील क्रियाएँ approval queue से गुज़रती हैं; Emergency Stop owner-only है और सब कुछ रोकता है।",
                "Sensitive actions pass through the approval queue; Emergency Stop is owner-only and halts everything.",
              )}
            </span>
          </div>
        </div>

        <details style={{ margin: "14px 0" }}>
          <summary style={{ cursor: "pointer", fontSize: 13 }}>
            {t(locale, "स्थिति बैज का अर्थ", "What the state badges mean")}
          </summary>
          <div style={{ display: "grid", gap: 6, marginTop: 8 }}>
            {legend.map((s) => (
              <CapabilityStateBadge key={s} status={s} locale={locale === "hi" ? "hi" : "en"} verbose />
            ))}
          </div>
        </details>

        {saveError ? (
          <p role="alert" style={{ color: "#dc2626", fontSize: 13 }}>
            {t(
              locale,
              "स्थिति सहेजी नहीं जा सकी — दोबारा कोशिश करें।",
              "Could not save the flag — please retry.",
            )}
          </p>
        ) : null}

        <button
          type="button"
          className="ss-btn-primary"
          disabled={saving}
          onClick={() => void complete()}
          style={{ width: "100%", padding: 12, fontSize: 15 }}
        >
          {saving
            ? t(locale, "सहेजा जा रहा है…", "Saving…")
            : t(locale, "समझ गया — शुरू करें", "Understood — get started")}
        </button>
      </section>
    </div>
  );
}
